/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Viewport Position Hook
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Provides viewport-aware positioning for overlays, tooltips, dropdowns, and modals.
 * Ensures content stays within visible bounds across all device sizes.
 * 
 * FEATURES:
 * - Automatic flip/shift when near viewport edges
 * - Safe area awareness (notches, status bars)
 * - Device-responsive constraints
 * - Smooth repositioning on scroll/resize
 * 
 * USAGE:
 * const { position, style } = useViewportPosition(triggerRef, {
 *   preferredPosition: 'top',
 *   offset: 8,
 *   maxWidth: 280,
 *   maxHeight: 400,
 * });
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useResponsive, BREAKPOINTS } from './useResponsive';

// Position types
export const POSITIONS = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  TOP_LEFT: 'top-left',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_RIGHT: 'bottom-right',
  CENTER: 'center',
};

// Default configuration
const DEFAULT_CONFIG = {
  preferredPosition: POSITIONS.TOP,
  offset: 8,                    // Gap between trigger and content
  padding: 12,                  // Minimum distance from viewport edge
  maxWidth: 320,
  maxHeight: 400,
  followTrigger: true,          // Update position when trigger moves
  constrainToViewport: true,    // Prevent overflow
  flipOnOverflow: true,         // Flip to opposite side if no space
  shiftOnOverflow: true,        // Shift along axis to fit
  usePortal: true,              // For fixed positioning (portal use)
};

/**
 * Calculate position and styles for viewport-constrained overlay
 * 
 * @param {React.RefObject} triggerRef - Ref to the trigger element
 * @param {Object} config - Configuration options
 * @returns {Object} { position, style, isFlipped, isShifted, actualPosition }
 */
export const useViewportPosition = (triggerRef, config = {}) => {
  // Memoize options using stable serialization
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [JSON.stringify(config)]);
  const responsive = useResponsive();
  
  const [positionState, setPositionState] = useState({
    position: options.preferredPosition,
    actualPosition: options.preferredPosition,
    style: {},
    isFlipped: false,
    isShifted: false,
    isReady: false,
  });
  
  const rafRef = useRef(null);
  
  const calculatePosition = useCallback(() => {
    if (!triggerRef?.current) {
      return;
    }
    
    const trigger = triggerRef.current.getBoundingClientRect();
    const {
      width: viewportWidth,
      height: viewportHeight,
      safeAreaInsets,
    } = responsive;
    
    const {
      preferredPosition,
      offset,
      padding,
      maxWidth,
      maxHeight,
      constrainToViewport,
      flipOnOverflow,
      shiftOnOverflow,
      usePortal,
    } = options;
    
    // Effective boundaries accounting for safe areas
    const bounds = {
      top: padding + safeAreaInsets.top,
      right: viewportWidth - padding - safeAreaInsets.right,
      bottom: viewportHeight - padding - safeAreaInsets.bottom,
      left: padding + safeAreaInsets.left,
    };
    
    // Calculate available space in each direction
    const space = {
      top: trigger.top - bounds.top,
      bottom: bounds.bottom - trigger.bottom,
      left: trigger.left - bounds.left,
      right: bounds.right - trigger.right,
    };
    
    // Constrain dimensions to viewport
    const contentWidth = Math.min(maxWidth, bounds.right - bounds.left);
    const contentHeight = Math.min(maxHeight, bounds.bottom - bounds.top);
    
    let x, y;
    let actualPosition = preferredPosition;
    let isFlipped = false;
    let isShifted = false;
    
    // Determine base position
    switch (preferredPosition) {
      case POSITIONS.TOP:
      case POSITIONS.TOP_LEFT:
      case POSITIONS.TOP_RIGHT:
        if (flipOnOverflow && space.top < contentHeight && space.bottom > space.top) {
          // Flip to bottom
          actualPosition = preferredPosition.replace('top', 'bottom');
          y = trigger.bottom + offset;
          isFlipped = true;
        } else {
          y = trigger.top - offset - contentHeight;
        }
        break;
        
      case POSITIONS.BOTTOM:
      case POSITIONS.BOTTOM_LEFT:
      case POSITIONS.BOTTOM_RIGHT:
        if (flipOnOverflow && space.bottom < contentHeight && space.top > space.bottom) {
          // Flip to top
          actualPosition = preferredPosition.replace('bottom', 'top');
          y = trigger.top - offset - contentHeight;
          isFlipped = true;
        } else {
          y = trigger.bottom + offset;
        }
        break;
        
      case POSITIONS.LEFT:
        if (flipOnOverflow && space.left < contentWidth && space.right > space.left) {
          actualPosition = POSITIONS.RIGHT;
          x = trigger.right + offset;
          isFlipped = true;
        } else {
          x = trigger.left - offset - contentWidth;
        }
        y = trigger.top + trigger.height / 2 - contentHeight / 2;
        break;
        
      case POSITIONS.RIGHT:
        if (flipOnOverflow && space.right < contentWidth && space.left > space.right) {
          actualPosition = POSITIONS.LEFT;
          x = trigger.left - offset - contentWidth;
          isFlipped = true;
        } else {
          x = trigger.right + offset;
        }
        y = trigger.top + trigger.height / 2 - contentHeight / 2;
        break;
        
      case POSITIONS.CENTER:
        x = viewportWidth / 2 - contentWidth / 2;
        y = viewportHeight / 2 - contentHeight / 2;
        break;
        
      default:
        y = trigger.top - offset - contentHeight;
    }
    
    // Calculate horizontal position for top/bottom positions
    if (actualPosition.includes('top') || actualPosition.includes('bottom')) {
      if (actualPosition.includes('left')) {
        x = trigger.left;
      } else if (actualPosition.includes('right')) {
        x = trigger.right - contentWidth;
      } else {
        // Center horizontally
        x = trigger.left + trigger.width / 2 - contentWidth / 2;
      }
    }
    
    // Apply shift constraints
    if (constrainToViewport && shiftOnOverflow) {
      // Horizontal shift
      if (x < bounds.left) {
        x = bounds.left;
        isShifted = true;
      } else if (x + contentWidth > bounds.right) {
        x = bounds.right - contentWidth;
        isShifted = true;
      }
      
      // Vertical shift
      if (y < bounds.top) {
        y = bounds.top;
        isShifted = true;
      } else if (y + contentHeight > bounds.bottom) {
        y = bounds.bottom - contentHeight;
        isShifted = true;
      }
    }
    
    // Build style object
    const style = usePortal ? {
      position: 'fixed',
      left: `${Math.round(x)}px`,
      top: `${Math.round(y)}px`,
      maxWidth: `${contentWidth}px`,
      maxHeight: `${contentHeight}px`,
      zIndex: 10000,
    } : {
      position: 'absolute',
      left: `${Math.round(x - trigger.left)}px`,
      top: `${Math.round(y - trigger.top)}px`,
      maxWidth: `${contentWidth}px`,
      maxHeight: `${contentHeight}px`,
    };
    
    setPositionState({
      position: preferredPosition,
      actualPosition,
      style,
      isFlipped,
      isShifted,
      isReady: true,
      triggerRect: trigger,
      contentDimensions: { width: contentWidth, height: contentHeight },
    });
    
  }, [triggerRef, options, responsive]);
  
  // Initial calculation and updates
  useEffect(() => {
    calculatePosition();
    
    if (!options.followTrigger) return;
    
    // Debounced recalculation
    const handleUpdate = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(calculatePosition);
    };
    
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [calculatePosition, options.followTrigger]);
  
  return positionState;
};

/**
 * Get CSS transform origin based on position
 * Useful for animated entrances
 */
export const getTransformOrigin = (position) => {
  const origins = {
    [POSITIONS.TOP]: 'bottom center',
    [POSITIONS.BOTTOM]: 'top center',
    [POSITIONS.LEFT]: 'right center',
    [POSITIONS.RIGHT]: 'left center',
    [POSITIONS.TOP_LEFT]: 'bottom left',
    [POSITIONS.TOP_RIGHT]: 'bottom right',
    [POSITIONS.BOTTOM_LEFT]: 'top left',
    [POSITIONS.BOTTOM_RIGHT]: 'top right',
    [POSITIONS.CENTER]: 'center center',
  };
  return origins[position] || 'center center';
};

/**
 * Get animation direction based on position (for motion)
 */
export const getAnimationDirection = (position, isFlipped) => {
  const baseDirections = {
    [POSITIONS.TOP]: { y: 10 },
    [POSITIONS.BOTTOM]: { y: -10 },
    [POSITIONS.LEFT]: { x: 10 },
    [POSITIONS.RIGHT]: { x: -10 },
    [POSITIONS.TOP_LEFT]: { y: 10, x: 5 },
    [POSITIONS.TOP_RIGHT]: { y: 10, x: -5 },
    [POSITIONS.BOTTOM_LEFT]: { y: -10, x: 5 },
    [POSITIONS.BOTTOM_RIGHT]: { y: -10, x: -5 },
    [POSITIONS.CENTER]: { scale: 0.95 },
  };
  
  const direction = baseDirections[position] || { y: 10 };
  
  // Invert if flipped
  if (isFlipped) {
    if (direction.y) direction.y *= -1;
    if (direction.x) direction.x *= -1;
  }
  
  return direction;
};

/**
 * Preset configurations for common overlay types
 */
export const OVERLAY_PRESETS = {
  tooltip: {
    preferredPosition: POSITIONS.TOP,
    offset: 8,
    padding: 8,
    maxWidth: 280,
    maxHeight: 200,
    flipOnOverflow: true,
    shiftOnOverflow: true,
  },
  dropdown: {
    preferredPosition: POSITIONS.BOTTOM_LEFT,
    offset: 4,
    padding: 12,
    maxWidth: 320,
    maxHeight: 400,
    flipOnOverflow: true,
    shiftOnOverflow: true,
  },
  popover: {
    preferredPosition: POSITIONS.BOTTOM,
    offset: 8,
    padding: 16,
    maxWidth: 360,
    maxHeight: 480,
    flipOnOverflow: true,
    shiftOnOverflow: true,
  },
  menu: {
    preferredPosition: POSITIONS.BOTTOM_LEFT,
    offset: 4,
    padding: 8,
    maxWidth: 240,
    maxHeight: 320,
    flipOnOverflow: true,
    shiftOnOverflow: false,
  },
  glossary: {
    preferredPosition: POSITIONS.TOP,
    offset: 8,
    padding: 16,
    maxWidth: 280,
    maxHeight: 400,
    flipOnOverflow: true,
    shiftOnOverflow: true,
  },
};

export default useViewportPosition;
